import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { useApiKey } from '../contexts/ApiKeyContext';
import { 
  RefreshCw, 
  AlertCircle, 
  CheckCircle, 
  Loader2, 
  Sparkles,
  Activity,
  TrendingUp,
  TrendingDown,
  Heart,
  Wrench,
  Eye,
  Play,
  HelpCircle,
  Settings,
  History,
  Bell,
  Globe,
  Clock,
  BarChart3,
  Zap,
  Shield,
  Target
} from 'lucide-react';

const AgentSelfHealing = ({ onShowHelp }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [healingStrategy, setHealingStrategy] = useState('suggest');
  const [healingInProgress, setHealingInProgress] = useState(false);
  const [healingSuggestion, setHealingSuggestion] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  
  // New state for enhanced features
  const [showWebhookConfig, setShowWebhookConfig] = useState(false);
  const [showPromptHistory, setShowPromptHistory] = useState(false);
  const [promptRevisions, setPromptRevisions] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookStatus, setWebhookStatus] = useState('idle');
  const [autoScanEnabled, setAutoScanEnabled] = useState(false);
  const [scanInterval, setScanInterval] = useState(3600000); // 1 hour default
  const [healingConfig, setHealingConfig] = useState({
    poor_performance_threshold: 75,
    critical_threshold: 60,
    min_feedback_count: 5,
    scan_days: 7
  });
  
  // Get API key from context
  const { apiKey, error: apiKeyError } = useApiKey();

  // Fetch user's agents
  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(getApiUrl('/user/agents'), {
        withCredentials: true
      });
      
      if (response.data && response.data.agents) {
        setAgents(response.data.agents);
        console.log(`✅ Fetched ${response.data.count} agents for user`);
      } else {
        setAgents([]);
        console.log('✅ No agents found for user');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to fetch agents');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch prompt revision history for an agent
  const fetchPromptHistory = async (agentId) => {
    try {
      const response = await axios.get(getApiUrl(`/agent/${agentId}/prompt/history`), {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 200) {
        setPromptRevisions(response.data.revisions || []);
      }
    } catch (err) {
      console.error('Error fetching prompt history:', err);
      setError('Failed to fetch prompt history');
    }
  };

  // Fetch webhook configuration
  const fetchWebhookConfig = async () => {
    try {
      const response = await axios.get(getApiUrl('/user/webhook-config'), {
        withCredentials: true
      });
      
      if (response.status === 200) {
        setWebhookUrl(response.data.webhook_url || '');
        setWebhookStatus(response.data.status || 'idle');
      }
    } catch (err) {
      console.error('Error fetching webhook config:', err);
    }
  };

  // Update webhook configuration
  const updateWebhookConfig = async () => {
    try {
      setWebhookStatus('updating');
      
      const response = await axios.put(getApiUrl('/user/webhook-config'), {
        webhook_url: webhookUrl
      }, {
        withCredentials: true
      });
      
      if (response.status === 200) {
        setSuccess('Webhook configuration updated successfully!');
        setWebhookStatus('active');
        setShowWebhookConfig(false);
      }
    } catch (err) {
      console.error('Error updating webhook config:', err);
      setError('Failed to update webhook configuration');
      setWebhookStatus('error');
    }
  };

  // Test webhook connection
  const testWebhook = async () => {
    try {
      setWebhookStatus('testing');
      
      const response = await axios.post(getApiUrl('/user/webhook-test'), {
        webhook_url: webhookUrl
      }, {
        withCredentials: true
      });
      
      if (response.status === 200) {
        setSuccess('Webhook test successful!');
        setWebhookStatus('active');
      }
    } catch (err) {
      console.error('Error testing webhook:', err);
      setError('Webhook test failed');
      setWebhookStatus('error');
    }
  };

  // Trigger self-healing scan
  const triggerHealingScan = async () => {
    try {
      setScanning(true);
      setError('');
      setSuccess('');
      
      if (!apiKey) {
        throw new Error('API key not available');
      }
      
      const response = await axios.post(
        getApiUrl('/self-healing/scan'),
        {},
        {
          headers: {
            'x-api-key': apiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        setScanResults(response.data);
        setSuccess('Self-healing scan completed successfully!');
        fetchAgents(); // Refresh agents to get updated healing status
      }
    } catch (err) {
      console.error('Error triggering healing scan:', err);
      setError(err.response?.data?.error || 'Failed to trigger healing scan');
    } finally {
      setScanning(false);
    }
  };

  // Heal a specific agent
  const healAgent = async (agentId, strategy = 'suggest') => {
    try {
      setHealingInProgress(true);
      setError('');
      setSuccess('');
      
      if (!apiKey) {
        throw new Error('API key not available');
      }
      
      const response = await axios.post(
        getApiUrl('/agent/self_heal'),
        {
          agent_id: agentId,
          strategy: strategy
        },
        {
          headers: {
            'x-api-key': apiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        const result = response.data;
        
        if (strategy === 'suggest') {
          setHealingSuggestion(result);
          setSelectedAgent(agents.find(a => a.id === agentId));
        } else {
          setSuccess(`Agent "${result.agent_name}" healed successfully!`);
          setHealingSuggestion(null);
          fetchAgents(); // Refresh agents
        }
      }
    } catch (err) {
      console.error('Error healing agent:', err);
      setError(err.response?.data?.error || 'Failed to heal agent');
    } finally {
      setHealingInProgress(false);
    }
  };

  // Apply healing suggestion
  const applyHealingSuggestion = async () => {
    if (!healingSuggestion || !selectedAgent) return;
    
    try {
      setHealingInProgress(true);
      setError('');
      
      if (!apiKey) {
        throw new Error('API key not available');
      }
      
      const response = await axios.post(
        getApiUrl('/agent/self_heal'),
        {
          agent_id: selectedAgent.id,
          strategy: 'auto'
        },
        {
          headers: {
            'x-api-key': apiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        setSuccess(`Agent "${response.data.agent_name}" healed successfully!`);
        setHealingSuggestion(null);
        setSelectedAgent(null);
        fetchAgents(); // Refresh agents
      }
    } catch (err) {
      console.error('Error applying healing suggestion:', err);
      setError(err.response?.data?.error || 'Failed to apply healing suggestion');
    } finally {
      setHealingInProgress(false);
    }
  };

  // Get agents that need healing
  const agentsNeedingHealing = agents.filter(agent => agent.healing_recommended);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-400';
      case 'poor': return 'text-orange-400';
      case 'declining': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  // Auto-scan functionality
  useEffect(() => {
    let interval;
    if (autoScanEnabled && scanInterval > 0) {
      interval = setInterval(() => {
        triggerHealingScan();
      }, scanInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoScanEnabled, scanInterval]);

  useEffect(() => {
    fetchAgents();
    fetchWebhookConfig();
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-gray-900 rounded-xl shadow-lg p-6 border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Agent Self-Healing</h1>
              <p className="text-gray-400">
                Monitor and automatically improve your AI agents' performance using feedback analysis and prompt optimization.
              </p>
            </div>
            <div className="flex space-x-3">
              {onShowHelp && (
                <button
                  onClick={onShowHelp}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-300"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>Help</span>
                </button>
              )}
              <button
                onClick={() => setShowWebhookConfig(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300"
              >
                <Globe className="w-4 h-4" />
                <span>Webhooks</span>
              </button>
              <button
                onClick={triggerHealingScan}
                disabled={scanning || !apiKey}
                className="flex items-center space-x-2 bg-[#7B61FF] text-white px-4 py-2 rounded-xl hover:bg-[#6B51EF] transition-colors duration-300 disabled:opacity-50"
              >
                {scanning ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Activity className="w-4 h-4" />
                )}
                <span>{scanning ? 'Scanning...' : 'Run Healing Scan'}</span>
              </button>
              <button
                onClick={fetchAgents}
                className="flex items-center space-x-2 bg-gray-700 text-white px-4 py-2 rounded-xl hover:bg-gray-600 transition-colors duration-300"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Real-time Monitoring Dashboard */}
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-purple-400" />
              Real-time Monitoring
            </h3>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={autoScanEnabled}
                  onChange={(e) => setAutoScanEnabled(e.target.checked)}
                  className="rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-purple-500"
                />
                <span>Auto-scan</span>
              </label>
              <select
                value={scanInterval}
                onChange={(e) => setScanInterval(parseInt(e.target.value))}
                className="bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-1"
              >
                <option value={900000}>15 minutes</option>
                <option value={1800000}>30 minutes</option>
                <option value={3600000}>1 hour</option>
                <option value={7200000}>2 hours</option>
                <option value={14400000}>4 hours</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-green-400">{agents.length}</div>
              <div className="text-sm text-gray-400">Total Agents</div>
            </div>
            <div className="text-center p-3 bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-orange-400">{agentsNeedingHealing.length}</div>
              <div className="text-sm text-gray-400">Need Healing</div>
            </div>
            <div className="text-center p-3 bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-blue-400">
                {webhookStatus === 'active' ? '✓' : webhookStatus === 'error' ? '✗' : '—'}
              </div>
              <div className="text-sm text-gray-400">Webhook Status</div>
            </div>
            <div className="text-center p-3 bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-purple-400">
                {autoScanEnabled ? '✓' : '—'}
              </div>
              <div className="text-sm text-gray-400">Auto-scan</div>
            </div>
          </div>
        </div>

        {/* Self-Healing Guide */}
        <div className="mb-6 p-4 bg-gray-800 border border-gray-600 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Heart className="w-5 h-5 mr-2 text-red-400" />
              How Self-Healing Works
            </h3>
            <button
              onClick={() => setShowGuide(!showGuide)}
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              {showGuide ? 'Hide' : 'Show'} Details
            </button>
          </div>
          
          {showGuide && (
            <div className="space-y-4 text-sm text-gray-300">
              <div className="grid md:grid-cols-2 gap-6">
                {/* How It Works */}
                <div>
                  <h4 className="font-semibold text-blue-400 mb-3">🔍 How Self-Healing Works</h4>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium">1. Performance Analysis</span>
                        <p className="text-xs text-gray-400">Analyzes recent feedback logs using Claude AI</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium">2. Issue Detection</span>
                        <p className="text-xs text-gray-400">Identifies specific problems (hallucination, tone, etc.)</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium">3. Prompt Generation</span>
                        <p className="text-xs text-gray-400">Creates improved system prompts using Claude</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium">4. Application</span>
                        <p className="text-xs text-gray-400">Applies or suggests the improved prompts</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* When to Use */}
                <div>
                  <h4 className="font-semibold text-green-400 mb-3">🎯 When to Use Self-Healing</h4>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-green-400">✅ Good Candidates:</span>
                        <p className="text-xs text-gray-400">Agents with consistent poor performance (score &lt; 75)</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-green-400">✅ Good Candidates:</span>
                        <p className="text-xs text-gray-400">Agents with declining performance trends</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-red-400">❌ Not Suitable:</span>
                        <p className="text-xs text-gray-400">Agents performing excellently (score &gt; 85)</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-red-400">❌ Not Suitable:</span>
                        <p className="text-xs text-gray-400">Agents with insufficient feedback data</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Strategies */}
              <div className="border-t border-gray-600 pt-4">
                <h4 className="font-semibold text-purple-400 mb-3">⚙️ Healing Strategies</h4>
                <div className="grid md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="font-medium text-purple-400 mb-2">💡 Suggest Mode:</p>
                    <p className="text-gray-400">Generates improvement suggestions for manual review and approval. Best for testing changes before applying.</p>
                  </div>
                  <div>
                    <p className="font-medium text-purple-400 mb-2">🚀 Auto Mode:</p>
                    <p className="text-gray-400">Automatically applies the improved prompts. Best for trusted agents with clear performance issues.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-900 border border-red-700 rounded-xl">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-red-300">{error}</span>
            </div>
          </div>
        )}

        {/* Success Display */}
        {success && (
          <div className="mb-6 p-4 bg-green-900 border border-green-700 rounded-xl">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
              <span className="text-green-300">{success}</span>
            </div>
          </div>
        )}

        {/* Scan Results Summary */}
        {scanResults && (
          <div className="mb-6 p-4 bg-blue-900 border border-blue-700 rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-3">Last Scan Results</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{scanResults.total_agents_scanned}</div>
                <div className="text-sm text-gray-400">Agents Scanned</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400">{scanResults.summary?.critical_agents || 0}</div>
                <div className="text-sm text-gray-400">Critical Issues</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">{scanResults.summary?.poor_performance_agents || 0}</div>
                <div className="text-sm text-gray-400">Poor Performance</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{scanResults.summary?.prompt_updates_generated || 0}</div>
                <div className="text-sm text-gray-400">Updates Generated</div>
              </div>
            </div>
          </div>
        )}

        {/* Agents Needing Healing */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <Heart className="w-5 h-5 mr-2 text-red-400" />
            Agents Needing Healing ({agentsNeedingHealing.length})
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#7B61FF] mx-auto mb-4" />
              <p className="text-gray-400">Loading agents...</p>
            </div>
          ) : agentsNeedingHealing.length === 0 ? (
            <div className="text-center py-12 bg-gray-800 rounded-xl border border-gray-700">
              <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <p className="text-gray-300 font-medium mb-2">All agents are performing well!</p>
              <p className="text-gray-400 text-sm">No agents currently need healing.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agentsNeedingHealing.map((agent) => (
                <div key={agent.id} className="bg-gray-800 border border-orange-500/30 rounded-xl p-6 hover:border-orange-500/50 transition-colors duration-300">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{agent.name}</h3>
                      {agent.description && (
                        <p className="text-gray-400 text-sm mb-3">{agent.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          fetchPromptHistory(agent.id);
                          setShowPromptHistory(true);
                        }}
                        className="p-2 text-purple-400 hover:text-purple-300 hover:bg-purple-900/20 rounded-lg transition-colors"
                        title="View Prompt History"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => healAgent(agent.id, 'suggest')}
                        disabled={healingInProgress}
                        className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                        title="Get Healing Suggestion"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => healAgent(agent.id, 'auto')}
                        disabled={healingInProgress}
                        className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                        title="Auto-Heal Agent"
                      >
                        <Wrench className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Type:</span>
                      <span className="text-white font-medium">{agent.agent_type}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">LLM:</span>
                      <span className="text-white font-medium">{agent.llm}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Created:</span>
                      <span className="text-white">{formatDate(agent.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span className="text-orange-400 font-medium flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Needs Healing
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* All Agents */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-blue-400" />
            All Agents ({agents.length})
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#7B61FF] mx-auto mb-4" />
              <p className="text-gray-400">Loading agents...</p>
            </div>
          ) : agents.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No agents found. Register your first agent to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agents.map((agent) => (
                <div key={agent.id} className={`bg-gray-800 border rounded-xl p-6 transition-colors duration-300 ${
                  agent.healing_recommended 
                    ? 'border-orange-500/30 hover:border-orange-500/50' 
                    : 'border-gray-700 hover:border-[#7B61FF]'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{agent.name}</h3>
                      {agent.description && (
                        <p className="text-gray-400 text-sm mb-3">{agent.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          fetchPromptHistory(agent.id);
                          setShowPromptHistory(true);
                        }}
                        className="p-2 text-purple-400 hover:text-purple-300 hover:bg-purple-900/20 rounded-lg transition-colors"
                        title="View Prompt History"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      {agent.healing_recommended && (
                        <button
                          onClick={() => healAgent(agent.id, 'suggest')}
                          disabled={healingInProgress}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="Get Healing Suggestion"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      {agent.healing_recommended && (
                        <button
                          onClick={() => healAgent(agent.id, 'auto')}
                          disabled={healingInProgress}
                          className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="Auto-Heal Agent"
                        >
                          <Wrench className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Type:</span>
                      <span className="text-white font-medium">{agent.agent_type}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">LLM:</span>
                      <span className="text-white font-medium">{agent.llm}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Created:</span>
                      <span className="text-white">{formatDate(agent.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Status:</span>
                      {agent.healing_recommended ? (
                        <span className="text-orange-400 font-medium flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Needs Healing
                        </span>
                      ) : (
                        <span className="text-green-400 font-medium flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Healthy
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Webhook Configuration Modal */}
        {showWebhookConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl mx-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Webhook Configuration</h2>
                <button
                  onClick={() => setShowWebhookConfig(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="https://your-domain.com/webhook"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Receive real-time notifications when agents need healing or are automatically healed.
                  </p>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-white">Status</div>
                    <div className="text-sm text-gray-400">
                      {webhookStatus === 'active' ? 'Active' : webhookStatus === 'error' ? 'Error' : 'Not configured'}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={testWebhook}
                      disabled={!webhookUrl || webhookStatus === 'testing'}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {webhookStatus === 'testing' ? 'Testing...' : 'Test'}
                    </button>
                    <button
                      onClick={updateWebhookConfig}
                      disabled={!webhookUrl || webhookStatus === 'updating'}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {webhookStatus === 'updating' ? 'Updating...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prompt History Modal */}
        {showPromptHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Prompt Revision History</h2>
                <button
                  onClick={() => setShowPromptHistory(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              {promptRevisions.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No prompt revisions found for this agent.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {promptRevisions.map((revision, index) => (
                    <div key={revision.id} className="bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">
                              {revision.strategy === 'auto' ? 'Auto-Healing' : 'Manual Suggestion'}
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatDate(revision.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {revision.reason}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm font-medium text-gray-300 mb-2">Previous Prompt</div>
                          <div className="bg-gray-800 rounded p-3">
                            <pre className="text-xs text-gray-400 whitespace-pre-wrap">{revision.old_prompt || 'No previous prompt'}</pre>
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-300 mb-2">Updated Prompt</div>
                          <div className="bg-gray-800 rounded p-3">
                            <pre className="text-xs text-gray-400 whitespace-pre-wrap">{revision.new_prompt}</pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Healing Suggestion Modal */}
        {healingSuggestion && selectedAgent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Healing Suggestion for {selectedAgent.name}</h2>
                <button
                  onClick={() => setHealingSuggestion(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Analysis Summary */}
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Performance Analysis</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-gray-400">Average Score</div>
                      <div className="text-xl font-bold text-white">{healingSuggestion.analysis?.average_score?.toFixed(1) || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Feedback Count</div>
                      <div className="text-xl font-bold text-white">{healingSuggestion.analysis?.feedback_count || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Recent Issues</div>
                      <div className="text-sm text-white">
                        {healingSuggestion.analysis?.recent_issues?.slice(0, 2).map(issue => issue.issue).join(', ') || 'None'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Strategy</div>
                      <div className="text-sm text-white capitalize">{healingSuggestion.strategy}</div>
                    </div>
                  </div>
                </div>

                {/* Current vs Suggested Prompt */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Current Prompt</h3>
                    <div className="bg-gray-700 rounded-lg p-4">
                      <pre className="text-sm text-gray-300 whitespace-pre-wrap">{healingSuggestion.current_prompt || 'No current prompt'}</pre>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Suggested Improved Prompt</h3>
                    <div className="bg-gray-700 rounded-lg p-4">
                      <pre className="text-sm text-gray-300 whitespace-pre-wrap">{healingSuggestion.suggested_prompt}</pre>
                    </div>
                  </div>
                </div>

                {/* Diff View */}
                {healingSuggestion.diff && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Changes Summary</h3>
                    <div className="bg-gray-700 rounded-lg p-4">
                      <pre className="text-sm text-gray-300 whitespace-pre-wrap">{healingSuggestion.diff}</pre>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setHealingSuggestion(null)}
                  className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={applyHealingSuggestion}
                  disabled={healingInProgress}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {healingInProgress ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Apply Healing
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentSelfHealing; 